import React, { useState, useEffect, useMemo } from 'react';
import { TourProvider } from '@reactour/tour';
import { useTour } from '@reactour/tour';
import { useTranslatedSteps } from '../stepsTour';
import { useGlobalTour } from '../../contexts/TourContext';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFnsJalali } from '@mui/x-date-pickers/AdapterDateFnsJalali';
import { format as formatJalali } from 'date-fns-jalali';
import { format as formatGregorian } from 'date-fns';
import { payments } from '../../services/api';
import { useTranslation } from '../../contexts/TranslationContext';
import { enqueueSnackbar } from 'notistack';
import VideoProvider from '../../components/VideoProvider';
import { videoUrls, videoTitles } from '../../utils/videoUrls';
import { formatPersianNumber, toPersianDigits } from '../../utils/validation';
import { PersianAdapterDateFnsJalali } from '../../utils/persianDateAdapter';

interface Payment {
  id: number;
  amount: number;
  plan: string;
  authority: string;
  ref_id: string | null;
  card_pan: string | null;
  payment_date: string | null;
  status: string;
}

const PaymentHistoryContent: React.FC = () => {
  const { setIsOpen } = useTour();
  const { setTourFunction } = useGlobalTour();
  const [paymentHistory, setPaymentHistory] = useState<Payment[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  // const { showError } = useError();
  const { t, currentLanguage } = useTranslation();
  let flag = false;
  const fetchPayments = async () => {
    try {
      const response = await payments.getPaymentHistory({
        start_date: startDate ? formatGregorian(startDate, "yyyy-MM-dd'T'HH:mm:ss'Z'") : undefined,
        end_date: endDate ? formatGregorian(endDate, "yyyy-MM-dd'T'HH:mm:ss'Z'") : undefined,
        skip: page * rowsPerPage,
        limit: rowsPerPage,
      });
      setPaymentHistory(response.data);
    } catch (error) {
      if (!flag) {
        enqueueSnackbar(t('profile.error.sessionExpired'), { 
          variant: 'error',
          autoHideDuration: 5000,
          style: {
            padding: '20px',
            marginTop: '20px', // Add margin top
          },
        });
        flag = true;
      }
      setTimeout(() => {
        localStorage.removeItem('token');
        window.location.href = '/auth/auth';
      }, 4000);
      // showError(error);
    }
  };

  // Register tour function with global tour context
  useEffect(() => {
    setTourFunction(() => setIsOpen(true));
  }, [setTourFunction, setIsOpen]);

  useEffect(() => {
    fetchPayments();
  }, [page, rowsPerPage, startDate, endDate]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatAmount = (amount: number) => {
    if (currentLanguage === 'fa') {
      return formatPersianNumber(amount) + t('payments.cash_type');
    }
    return amount + t('payments.cash_type');
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'success':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return t('payments.success');
      case 'pending':
        return t('payments.pending');
      case 'failed':
        return t('payments.failed');
      default:
        return status;
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return '---';
    const formatted = formatJalali(new Date(date), 'HH:mm yyyy/MM/dd');
    return currentLanguage === 'fa' ? toPersianDigits(formatted) : formatted;
  };

  const dateAdapter = useMemo(
    () => (currentLanguage === 'fa' ? PersianAdapterDateFnsJalali : AdapterDateFnsJalali),
    [currentLanguage],
  );

  const formatDisplayText = (value: string | null) => {
    if (!value) return '---';
    return currentLanguage === 'fa' ? toPersianDigits(value) : value;
  };

  // Helper to translate subscription plan
  const getPlanText = (plan: string) => {
    switch (plan) {
      case 'intern':
        return t('onboarding.subscription.intern');
      case 'expert':
        return t('onboarding.subscription.expert');
      case 'master':
        return t('onboarding.subscription.master');
      default:
        return plan;
    }
  };

  return (
    <Box dir={currentLanguage === 'fa' ? 'rtl' : 'ltr'} sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 3 }} className="first-step">
        {t('payments.title')}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', justifyContent: 'flex-start' }} className="second-step">
        <LocalizationProvider dateAdapter={dateAdapter}>
          <DatePicker
            label={t('payments.fromDate')}
            value={startDate}
            onChange={setStartDate}
            slotProps={{
              textField: {
                variant: 'outlined',
                size: 'small',
                fullWidth: true,
                InputLabelProps: {
                  sx: {
                    left: 8,
                    right: 'auto',
                    transformOrigin: 'left',
                    textAlign: 'left',
                    width: 'auto',
                  }
                },
                inputProps: {
                  style: { textAlign: 'left' }
                },
              },
            }}
          />
          <DatePicker
            label={t('payments.toDate')}
            value={endDate}
            onChange={setEndDate}
            slotProps={{
              textField: {
                variant: 'outlined',
                size: 'small',
                fullWidth: true,
                InputLabelProps: {
                  sx: {
                    left: 8,
                    right: 'auto',
                    transformOrigin: 'left',
                    textAlign: 'left',
                    width: 'auto',
                  }
                },
                inputProps: {
                  style: { textAlign: 'left' }
                },
              },
            }}
          />
        </LocalizationProvider>
      </Box>

      <Paper sx={{ p: 0, mb: 3, width: '100%', overflowX: 'auto' }}>
        <TableContainer className="third-step">
          <Table
            size="small"
            sx={{
              '& .MuiTableCell-root': {
                textAlign: 'center',
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>{t('payments.paymentId')}</TableCell>
                <TableCell>{t('payments.date')}</TableCell>
                <TableCell>{t('payments.amount')}</TableCell>
                <TableCell>{t('payments.plan')}</TableCell>
                <TableCell>{t('payments.cardNumber')}</TableCell>
                <TableCell>{t('payments.trackingCode')}</TableCell>
                <TableCell>{t('payments.status')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentHistory.map((payment) => (
                <TableRow key={payment.id}>
                  <TableCell>{payment.authority}</TableCell>
                  <TableCell>{formatDate(payment.payment_date)}</TableCell>
                  <TableCell>{formatAmount(payment.amount)}</TableCell>
                  <TableCell>{getPlanText(payment.plan)}</TableCell>
                  <TableCell>{formatDisplayText(payment.card_pan)}</TableCell>
                  <TableCell>{formatDisplayText(payment.ref_id)}</TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusText(payment.status)}
                      color={getStatusColor(payment.status)}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={-1}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[{ value: 10, label: formatPersianNumber(10) }, { value: 25, label: formatPersianNumber(25) }, { value: 50, label: formatPersianNumber(50) }]}
          labelRowsPerPage={t('payments.rowsPerPage')}
          labelDisplayedRows={({ from, to }) => `${formatPersianNumber(from)} - ${formatPersianNumber(to)}`}
        />
      </Paper>
    </Box>
  );
};

const PaymentHistory: React.FC = () => {
  return (
    <TourProvider steps={useTranslatedSteps().steps_payments} afterOpen={(target) => console.log('Tour opened for:', target)} beforeClose={(target) => console.log('Tour closing for:', target)}>
      <VideoProvider videoUrl={videoUrls.payments} videoTitle={videoTitles.payments}>
        <PaymentHistoryContent />
      </VideoProvider>
    </TourProvider>
  );
};

export default PaymentHistory;